import { Alert, Box, Divider, FormControl, FormLabel, Typography } from "@mui/material";
import { 
  ExternalMeetingInfo, MeetingPollPriority, MeetingQuestionAnswerSubmission, SelectedSlots 
} from "../../store";
import colors from "../../colors";
import { DateTime } from "luxon";
import { CabButton, CabIcon } from "@CabComponents";
import { IoAdd } from 'react-icons/io5';
import { getAnswerString } from "../../utils/scheduleUtils";
import { PollTableLegend } from "../../components/Meeting/GroupScheduling";

export type SuccessProps = {
  poll: ExternalMeetingInfo | undefined
  answers: { [email: string]: MeetingQuestionAnswerSubmission[] };
  votedSlots: { [email: string]: Map<number, SelectedSlots[number] | null> };
  participants: {email: string, name: string}[];
  onEditSelections: () => void;
  allDaySlots?: boolean;
};


const Success = ({poll, answers, votedSlots, participants, onEditSelections, allDaySlots = false}: SuccessProps) => {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: 32,
            fontWeight: 300,
            fontFamily: "sentient"
          }}>
          Thanks for submitting your selections!
        </Typography>
        <Typography variant="h5" color={colors.calmGrey600}>
          <b>{poll?.create_user.first_name} {poll?.create_user.last_name}</b> will
          send a meeting invite when all the results are in.
        </Typography>
      </Box>
      <Box
        sx={{
          padding: 5,
          border: '1px solid',
          borderColor: colors.black200,
          borderRadius: '12px',
          marginTop: 4,
          marginBottom: 5,
          backgroundColor: colors.white900
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'},
            justifyContent: 'space-between'
          }}>
          <Typography
            variant="h1"
            sx={{
              lineHeight: '36px',
              fontSize: 26
            }}>
            Your Selections
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: {xs: 'column', lg: 'row'}
            }}>
            <CabButton onClick={onEditSelections} sx={{maxWidth: '160px'}}>Edit Selections</CabButton>
            <CabButton
              sx={{maxWidth: '160px'}}
              onClick={() => window.location.reload()}
              buttonType="tertiary"
              color='primary'
              icon={<CabIcon Icon={IoAdd} alt='Add'/>}
            >
              Add Attendee
            </CabButton>
          </Box>
        </Box>

        {participants.map(({ name, email }, i) => {
          const filteredVotes = Array.from(votedSlots[email]?.values() || []).filter(
            slot => (slot?.priority != null && slot != null)
          ) as SelectedSlots;
          const sortedVotes = filteredVotes.sort((a, b) => (a.start > b.start) ? 1 : -1);
          const dates = [...new Set(sortedVotes.map(slot =>  
            slot.start.toLocaleString({ weekday: 'long', month: 'long', day: 'numeric'})))];

          return (
            <Box
              key={email}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
              {i !== 0 && <Divider sx={{ marginTop: 4 }} />}
              <Typography
                variant="h5"
                sx={{
                  marginTop: '12px',
                  lineHeight: '24px',
                  fontSize: 16
                }}>
                {name || email}
              </Typography>
              {dates.length ? (
                <Box>
                  <Box sx={{
                    width: 290
                  }}>
                    <PollTableLegend hidePopular />
                  </Box>
                  <Box>
                    {dates.map(date => {
                      return (
                        <Box key={date} sx={{
                          marginTop: 2
                        }}>
                          <Typography variant="body1" sx={{
                            marginBottom: 2
                          }}>{date}</Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1
                            }}>
                            {sortedVotes.filter(slot => 
                              slot.start.toLocaleString({ weekday: 'long', month: 'long', day: 'numeric'}) === date)
                              .map(dateSlot => {
                                return (
                                  <TimeSlot
                                    key={dateSlot.start.toString()}
                                    priority={dateSlot.priority || MeetingPollPriority.AVAILABLE}
                                    start={dateSlot.start}
                                    end={dateSlot.end}
                                    allDaySlots={allDaySlots}
                                  />
                                );
                              })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Alert severity="warning" sx={{marginTop: 2}}> None of the provided times work </Alert>
              )}
              {poll?.questions && poll.questions?.length > 0 && (
                <Box sx={{
                  marginTop: 2
                }}>
                  <Box>
                    <QuestionResponses questions={poll.questions} answers={answers[email] || []} />
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Success;

type TimeSlotProps = {
  priority: number;
  start: DateTime;
  end: DateTime;
  allDaySlots?: boolean;
};

const TimeSlot = ({priority, start, end, allDaySlots = false}: TimeSlotProps) => {
  return (
    <Box sx={{
      display: 'flex',
      backgroundColor: colors.white900,
      borderColor: colors.black200,
      borderWidth: 1,
      borderStyle: 'solid',
      borderRadius: 1,
      paddingLeft: 1,
      paddingRight: 1,
      justifyContent: 'space-between',
      textAlign: 'center',
      color: colors.black700,
      ...(priority === 1 ? {
        backgroundColor: colors.forest500,
        borderColor: colors.forest700,
        color: colors.black900,
      } : {}),
      ...(priority === 2 ? {
        backgroundColor: colors.chamomile300,
        borderColor: colors.chamomile700,
        color: colors.black900,
      } : {}),

    }} >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 1,
          paddingBottom: 1,
          width: 80,
          height: 58
        }}>
        {allDaySlots ? (
          <Box sx={{
            fontWeight: 500
          }}>{start.toFormat('M/d/yyyy')}</Box>
        ) : (
          <>
            <Box sx={{
              fontWeight: 500
            }}>{start.toFormat('h:mma').toLowerCase()}</Box>
            <Box sx={{
              fontWeight: 500
            }}>{end.toFormat('h:mma').toLowerCase()}</Box>
          </>
        )}
      </Box>
    </Box>
  );
};

type QuestionProps = {
  questions: ExternalMeetingInfo['questions'] | undefined
  answers: MeetingQuestionAnswerSubmission[];
};

const QuestionResponses = ({ questions, answers }: QuestionProps) => {
  return (
    <Box sx={{
      marginTop: 2
    }}>
      {questions?.slice().sort((a, b) => (a?.order || 0) - (b?.order || 0)).map((question) => {
        const answer = answers.find(a => a.question === question.id);
        return (
          <FormControl key={question.id} fullWidth>
            <FormLabel sx={{marginTop: 2}}>{question.required ? question.title + ' *' : question.title}</FormLabel>
            <Box sx={{width: '100%'}}>
              <Typography
                sx={{
                  marginTop: 2,
                  fontWeight: 600
                }}>
                {getAnswerString(answer, question, '- no answer -')}
              </Typography>
            </Box>
          </FormControl>
        );
      })}
    </Box>
  );
};
